import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me')
};

// Admin - Questions
export const questionAPI = {
  create: (data) => api.post('/admin/questions', data),
  getAll: (params) => api.get('/admin/questions', { params }),
  getStats: () => api.get('/admin/questions/stats'),
  getOne: (id) => api.get(`/admin/questions/${id}`),
  update: (id, data) => api.put(`/admin/questions/${id}`, data),
  delete: (id) => api.delete(`/admin/questions/${id}`),
  bulkDelete: (ids) => api.post('/admin/questions/bulk-delete', { ids })
};

// Admin - TryOuts
export const tryoutAdminAPI = {
  create: (data) => api.post('/admin/tryouts', data),
  getAll: () => api.get('/admin/tryouts'),
  getOne: (id) => api.get(`/admin/tryouts/${id}`),
  update: (id, data) => api.put(`/admin/tryouts/${id}`, data),
  delete: (id) => api.delete(`/admin/tryouts/${id}`),
  autoGenerate: (data) => api.post('/admin/tryouts/auto-generate', data),
  generatePaket: (data) => api.post('/admin/tryouts/generate-paket', data),
  generatePaketCustom: (data) => api.post('/admin/tryouts/generate-paket-custom', data),
  getQuestionCount: (params) => api.get('/admin/questions/count', { params }),
  getPaketTemplates: () => api.get('/admin/paket-templates'),
  getPaketAvailability: (program) => api.get(`/admin/paket-availability/${program}`),
  toggleStatus: (id) => api.patch(`/admin/tryouts/${id}/toggle`),
  // Subject config for question distribution
  setSubjectConfig: (id, data) => api.post(`/admin/tryouts/${id}/subject-config`, data),
  generateQuestions: (id) => api.post(`/admin/tryouts/${id}/generate-questions`),
  getQuestionStats: (id) => api.get(`/admin/tryouts/${id}/question-stats`),
  // Preview all questions
  previewQuestions: (id) => api.get(`/admin/tryouts/${id}/preview-questions`),
  // Fix question count tools
  fixQuestionCount: (id, data) => api.post(`/admin/tryouts/${id}/fix-question-count`, data),
  rebuildQuestions: (id) => api.post(`/admin/tryouts/${id}/rebuild-questions`),
  removeQuestions: (id, questionIds) => api.post(`/admin/tryouts/${id}/remove-questions`, questionIds),
  trimToCount: (id, targetCount) => api.post(`/admin/tryouts/${id}/trim-to-count?target_count=${targetCount}`)
};

// Admin - Event Try Outs
export const eventTryoutAPI = {
  create: (data) => api.post('/admin/event-tryouts', data),
  getAll: () => api.get('/admin/event-tryouts'),
  getOne: (id) => api.get(`/admin/event-tryouts/${id}`),
  update: (id, data) => api.put(`/admin/event-tryouts/${id}`, data),
  delete: (id) => api.delete(`/admin/event-tryouts/${id}`),
  toggleStatus: (id) => api.patch(`/admin/event-tryouts/${id}/toggle`),
  // Start/Stop controls
  start: (id) => api.post(`/admin/event-tryouts/${id}/start`),
  stop: (id) => api.post(`/admin/event-tryouts/${id}/stop`),
  reset: (id) => api.post(`/admin/event-tryouts/${id}/reset`),
  // Participants
  getParticipants: (eventId) => api.get(`/admin/event-tryouts/${eventId}/participants`),
  addParticipant: (eventId, data) => api.post(`/admin/event-tryouts/${eventId}/participants`, data),
  updateParticipant: (eventId, participantId, data) => api.put(`/admin/event-tryouts/${eventId}/participants/${participantId}`, data),
  deleteParticipant: (eventId, participantId) => api.delete(`/admin/event-tryouts/${eventId}/participants/${participantId}`),
  uploadParticipantsExcel: (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/event-tryouts/${eventId}/participants/upload-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  checkAccess: (eventId) => api.get(`/event-tryouts/${eventId}/check-access`)
};

// Admin - Packages
export const packageAdminAPI = {
  create: (data) => api.post('/admin/packages', data),
  getAll: () => api.get('/admin/packages'),
  getOne: (id) => api.get(`/admin/packages/${id}`),
  update: (id, data) => api.put(`/admin/packages/${id}`, data),
  delete: (id) => api.delete(`/admin/packages/${id}`),
  toggleStatus: (id) => api.patch(`/admin/packages/${id}/toggle`)
};

// Admin - Stats
export const statsAPI = {
  get: () => api.get('/admin/stats')
};

// Public endpoints (no auth required)
export const publicAPI = {
  getTryouts: (program) => api.get('/public/tryouts', { params: program ? { program } : {} }),
  getPackages: (params) => api.get('/public/packages', { params: params || {} }),
  getLeaderboard: (tryoutId) => api.get(`/public/leaderboard/${tryoutId}`),
  getZoomClasses: (program) => api.get('/public/zoom-classes', { params: program ? { program } : {} }),
  getLandingContent: (section) => api.get('/public/landing-content', { params: section ? { section } : {} }),
  getStats: () => api.get('/public/stats'),
  getLandingSettings: () => api.get('/public/landing-settings'),
  // Event Try Out (Public - no auth required)
  getActiveEvents: () => api.get('/public/event-tryouts/active'),
  verifyParticipant: (participantNumber) => api.post('/public/event-tryouts/verify-participant', { participant_number: participantNumber }),
  startEventExam: (participantNumber, eventId) => api.post('/public/event-tryouts/start-exam', { participant_number: participantNumber, event_id: eventId }),
  submitEventExam: (sessionId, answers) => {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('answers', JSON.stringify(answers));
    return api.post('/public/event-tryouts/submit-exam', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getEventResult: (resultId) => api.get(`/public/event-tryouts/result/${resultId}`)
};

// Student - TryOuts
export const tryoutAPI = {
  getAll: () => api.get('/tryouts'),
  getOne: (id) => api.get(`/tryouts/${id}`),
  getQuestions: (id) => api.get(`/tryouts/${id}/questions`),
  submit: (id, data) => api.post(`/tryouts/${id}/submit`, data),
  getMyEvents: () => api.get('/my-events'),
  checkAccess: (id) => api.get(`/tryouts/${id}/check-access`)
};

// Student - Results
export const resultAPI = {
  getAll: () => api.get('/results'),
  getOne: (id) => api.get(`/results/${id}`),
  getDetailed: (id) => api.get(`/results/${id}/detailed`),
  sendEmail: (id) => api.post(`/results/${id}/send-email`),
  exportPdf: (id) => `${BACKEND_URL}/api/results/${id}/pdf`,  // Returns URL for download
  // Export Excel (admin)
  exportExcelUrl: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return `${BACKEND_URL}/api/admin/export/results/excel${queryString ? '?' + queryString : ''}`;
  }
};

// Export API (Admin)
export const exportAPI = {
  resultsExcelUrl: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return `${BACKEND_URL}/api/admin/export/results/excel${queryString ? '?' + queryString : ''}`;
  },
  participantsExcelUrl: (eventId) => `${BACKEND_URL}/api/admin/export/participants/excel/${eventId}`
};

// Leaderboard
export const leaderboardAPI = {
  get: (tryoutId) => api.get(`/leaderboard/${tryoutId}`),
  getGlobal: (params) => api.get('/public/leaderboard/global', { params })
};

// Packages
export const packageAPI = {
  getAll: () => api.get('/packages')
};

// Categories
export const categoryAPI = {
  getAll: () => api.get('/categories')
};

// Admin - Promos
export const promoAPI = {
  create: (data) => api.post('/admin/promos', data),
  getAll: () => api.get('/admin/promos'),
  update: (id, data) => api.put(`/admin/promos/${id}`, data),
  delete: (id) => api.delete(`/admin/promos/${id}`)
};

// Admin - Zoom Classes
export const zoomClassAPI = {
  create: (data) => api.post('/admin/zoom-classes', data),
  getAll: () => api.get('/admin/zoom-classes'),
  update: (id, data) => api.put(`/admin/zoom-classes/${id}`, data),
  delete: (id) => api.delete(`/admin/zoom-classes/${id}`)
};

// Admin - Landing Content
export const landingContentAPI = {
  create: (data) => api.post('/admin/landing-content', data),
  getAll: (section) => api.get('/admin/landing-content', { params: section ? { section } : {} }),
  update: (id, data) => api.put(`/admin/landing-content/${id}`, data),
  delete: (id) => api.delete(`/admin/landing-content/${id}`),
  getPublic: (section) => api.get('/public/landing-content', { params: section ? { section } : {} })
};

// Admin - Landing Settings
export const landingSettingsAPI = {
  get: () => api.get('/admin/landing-settings'),
  update: (data) => api.put('/admin/landing-settings', data),
  getPublic: () => api.get('/public/landing-settings')
};

// Admin - File Upload
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadPaymentProof: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/payment-proof', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Admin - Users
export const usersAPI = {
  getAll: (role) => api.get('/admin/users', { params: role ? { role } : {} }),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`)
};

// Admin - Mapel Time Settings
export const mapelTimeAPI = {
  create: (data) => api.post('/admin/mapel-time-settings', data),
  getAll: (program) => api.get('/admin/mapel-time-settings', { params: program ? { program } : {} }),
  delete: (id) => api.delete(`/admin/mapel-time-settings/${id}`)
};

// Admin - Subjects (Mata Pelajaran)
export const subjectAPI = {
  create: (data) => api.post('/admin/subjects', data),
  getAll: (program) => api.get('/admin/subjects', { params: program ? { program } : {} }),
  update: (id, data) => api.put(`/admin/subjects/${id}`, data),
  delete: (id) => api.delete(`/admin/subjects/${id}`),
  getPublic: (program) => api.get('/public/subjects', { params: program ? { program } : {} })
};

// Admin - Categories (Kategori)  
export const categoryAdminAPI = {
  create: (data) => api.post('/admin/categories', data),
  getAll: (params) => api.get('/admin/categories', { params: params || {} }),
  update: (id, data) => api.put(`/admin/categories/${id}`, data),
  delete: (id) => api.delete(`/admin/categories/${id}`),
  getPublic: (params) => api.get('/public/categories', { params: params || {} })
};

// Admin - Import/Export Questions
export const questionImportExportAPI = {
  getTemplate: () => api.get('/admin/questions/template'),
  exportQuestions: (program) => api.get('/admin/questions/export', { params: program ? { program } : {} }),
  importQuestions: (questions) => api.post('/admin/questions/import', questions)
};

// Payment API
export const paymentAPI = {
  getClientKey: () => api.get('/payment/client-key'),
  create: (data) => api.post('/payment/create', data),
  getStatus: (orderId) => api.get(`/payment/status/${orderId}`),
  confirmMock: (orderId) => api.post(`/payment/confirm-mock/${orderId}`),
  getUserPurchases: () => api.get('/user/purchases')
};

// Subscription Tier API (Admin)
export const subscriptionTierAPI = {
  getAll: () => api.get('/admin/subscription-tiers'),
  getById: (id) => api.get(`/admin/subscription-tiers/${id}`),
  create: (data) => api.post('/admin/subscription-tiers', data),
  update: (id, data) => api.put(`/admin/subscription-tiers/${id}`, data),
  delete: (id) => api.delete(`/admin/subscription-tiers/${id}`),
  seed: () => api.post('/admin/seed/subscription-tiers'),
  getPublic: () => api.get('/public/subscription-tiers')
};

// User Subscription API
export const userSubscriptionAPI = {
  getCurrent: () => api.get('/user/subscription'),
  getAllAdmin: () => api.get('/admin/user-subscriptions')
};

// User Profile API
export const userProfileAPI = {
  get: () => api.get('/user/profile'),
  update: (data) => api.put('/user/profile', data),
  getStats: () => api.get('/user/stats')
};

// Access Control API
export const accessControlAPI = {
  checkTryoutAccess: (tryoutId) => api.get(`/tryouts/${tryoutId}/check-access`)
};

// Analytics API
export const analyticsAPI = {
  getAdminAnalytics: (days = 30) => api.get('/admin/analytics', { params: { days } }),
  exportResults: (params) => api.get('/admin/results/export', { params })
};

// Detailed Results API
export const detailedResultAPI = {
  get: (resultId) => api.get(`/results/${resultId}/detailed`)
};

// Question Import API
export const questionImportAPI = {
  batchImport: (questions) => api.post('/admin/questions/import', questions)
};

// Admin - Materi (Learning Materials) API
export const materiAPI = {
  upload: (formData) => api.post('/admin/materi/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll: (params) => api.get('/admin/materi', { params: params || {} }),
  getOne: (id) => api.get(`/admin/materi/${id}`),
  update: (id, data) => api.put(`/admin/materi/${id}`, data),
  delete: (id) => api.delete(`/admin/materi/${id}`),
  getForStudent: (params) => api.get('/materi', { params: params || {} })
};

// Admin - Email List API
export const emailListAPI = {
  add: (data) => api.post('/admin/email-list', data),
  getAll: (params) => api.get('/admin/email-list', { params: params || {} }),
  getCount: () => api.get('/admin/email-list/count'),
  import: (formData) => api.post('/admin/email-list/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/admin/email-list/${id}`)
};

// Admin - Admin Users Management API
export const adminUsersAPI = {
  getAll: () => api.get('/admin/admins'),
  create: (data) => api.post('/admin/admins', data),
  update: (id, data) => api.put(`/admin/admins/${id}`, data),
  delete: (id) => api.delete(`/admin/admins/${id}`)
};

// Admin - Exam Results API (Enhanced)
export const examResultsAPI = {
  getAll: (params) => api.get('/admin/exam-results', { params: params || {} }),
  export: (params) => api.get('/admin/exam-results/export', { params: params || {} }),
  print: (params) => api.get('/admin/exam-results/print', { params: params || {} })
};

// Admin - Purchase Management API
export const purchaseAdminAPI = {
  getPending: () => api.get('/admin/purchases/pending'),
  getAll: (params) => api.get('/admin/purchases/all', { params: params || {} }),
  confirm: (id) => api.post(`/admin/purchases/${id}/confirm`),
  reject: (id) => api.post(`/admin/purchases/${id}/reject`)
};

// User - Purchase API
export const purchaseAPI = {
  request: (data) => api.post('/purchases/request', data),
  getMyPurchases: () => api.get('/user/my-purchases')
};

// Auth - Change Password
export const changePasswordAPI = {
  change: (data) => api.post('/auth/change-password', data)
};

// eBooks API
export const ebookAPI = {
  getAll: () => api.get('/admin/ebooks'),
  create: (data) => api.post('/admin/ebooks', data),
  update: (id, data) => api.put(`/admin/ebooks/${id}`, data),
  delete: (id) => api.delete(`/admin/ebooks/${id}`),
  getPublic: (params) => api.get('/ebooks', { params })
};

// Testimonials API
export const testimonialAPI = {
  getAll: () => api.get('/admin/testimonials'),
  create: (data) => api.post('/admin/testimonials', data),
  update: (id, data) => api.put(`/admin/testimonials/${id}`, data),
  delete: (id) => api.delete(`/admin/testimonials/${id}`),
  getPublic: (params) => api.get('/testimonials', { params })
};

// Program Images API
export const programImageAPI = {
  getAll: () => api.get('/admin/program-images'),
  create: (data) => api.post('/admin/program-images', data),
  update: (id, data) => api.put(`/admin/program-images/${id}`, data),
  delete: (id) => api.delete(`/admin/program-images/${id}`),
  getPublic: () => api.get('/program-images'),
  seedDefaults: () => api.post('/admin/program-images/seed-defaults')
};

export default api;
